<template>
  <section id="add-user-block" v-loading="loading" class="case-creation">
    <div class="container-default">
      <div class="case-creation__step case-creation__patient-information">
        <div class="case-creation__card mb-10">
          <div class="case-creation__heading">
            <div class="title-with-icon">
              <span class="icon">
                <img
                  src="@/assets/images/icons/icon-patient-info.svg"
                  alt="icon"
                  class="img-fluid"
                />
              </span>
              <h3 class="title">
                {{ isNewUser ? "Add User" : "Update User" }}
              </h3>
            </div>
          </div>
          <div class="case-creation__body pb-0">
            <div
              class="vue-form"
              v-loading.fullscreen="loading"
              :element-loading-text="loadingText"
              element-loading-spinner="el-icon-loading"
              element-loading-background="rgba(0, 0, 0, 0.8)"
            >
              <el-form ref="form" v-model="formData">
                <el-row :gutter="20">
                  <el-col :span="8">
                    <div class="case-creation__input">
                      <el-form-item class="mb-15">
                        <label for="text">
                          User Type
                          <span class="star">*</span>
                        </label>

                        <el-select
                          placeholder="Select User Type"
                          v-model="formData.user_type"
                          @change="checkUserType"
                          filterable
                          default-first-option
                          :disabled="isDisabled"
                        >
                          <template v-if="!isBillingUser">
                            <el-option
                              v-for="(billing, index) in billingUsers"
                              :key="index"
                              :label="billing.label"
                              :value="billing.value"
                            >
                            </el-option>
                          </template>
                          <template v-else-if="isAccountManager">
                            <el-option
                              v-for="(facility, index) in facilityUsers"
                              :key="index"
                              :label="facility.label"
                              :value="facility.value"
                            >
                            </el-option>
                          </template>
                          <template v-else>
                            <el-option-group
                              v-for="group of getUserTypes"
                              :key="group.label"
                              :label="group.label"
                            >
                              <el-option
                                v-for="item in group.options"
                                :key="item.value"
                                :label="item.name"
                                :value="item.value"
                              >
                              </el-option>
                            </el-option-group>
                          </template>
                        </el-select>
                        <p
                          class="err"
                          v-if="
                            getUserAccountErrors &&
                            getUserAccountErrors.user_type
                          "
                        >
                          {{ getUserAccountErrors.user_type }}
                        </p>
                      </el-form-item>
                    </div>
                  </el-col>
                  <el-col
                    :span="8"
                    v-if="formData.user_type === 'HOSPITAL_PHYSICIAN'"
                  >
                    <div class="case-creation__input">
                      <el-form-item class="mb-15">
                        <label for="text">
                          Physician
                          <span class="star">*</span>
                        </label>
                        <el-select
                          v-model="formData.physician"
                          placeholder="Select Physician"
                          @change="setPhysicianData"
                          :filterable="true"
                          :disabled="!isNewUser"
                          default-first-option
                        >
                          <el-option
                            v-for="(physician, index) of physiciansData.data"
                            :key="index"
                            :value="physician._id"
                            :label="physician.name"
                            :disabled="isInactiveStatus(physician)"
                          >
                            <span style="float: left">{{
                              physician.name
                            }}</span>
                            <span
                              v-if="isInactiveStatus(physician)"
                              style="
                                float: right;
                                color: #eb5757;
                                font-size: 13px;
                                padding-left: 15px;
                              "
                            >
                              Inactive
                            </span>
                          </el-option>
                        </el-select>
                        <p
                          class="err"
                          v-if="
                            getUserAccountErrors &&
                            getUserAccountErrors.physician
                          "
                        >
                          {{ getUserAccountErrors.physician }}
                        </p>
                      </el-form-item>
                    </div>
                  </el-col>
                  <el-col :span="8" v-if="isLabUser">
                    <div class="case-creation__input">
                      <el-form-item class="mb-15">
                        <label for="text">
                          Labs
                          <span class="star">*</span>
                        </label>
                        <el-select
                          v-model="formData.labs"
                          placeholder="Select Labs"
                          @change="setLabDetails"
                          :filterable="true"
                          default-first-option
                          multiple
                        >
                          <el-option
                            v-for="(lab, index) of getAllLabs"
                            :key="index"
                            :value="lab._id"
                            :label="lab.name"
                          ></el-option>
                        </el-select>
                        <p
                          class="err"
                          v-if="
                            getUserAccountErrors && getUserAccountErrors.lab
                          "
                        >
                          {{ getUserAccountErrors.lab }}
                        </p>
                      </el-form-item>
                    </div>
                  </el-col>
                  <el-col
                    :span="8"
                    v-if="
                      formData.user_type && formData.user_type == 'MARKETER'
                    "
                  >
                    <div class="case-creation__input">
                      <el-form-item class="mb-15">
                        <label for="text">
                          Reporting To <span class="star">*</span></label
                        >
                        <el-select
                          v-model="formData.reporting_to"
                          placeholder="Select Manager"
                          :filterable="true"
                          multiple
                          collapse-tags
                          default-first-option
                        >
                          <el-option
                            v-for="(manager, index) of managersList"
                            :key="index"
                            :value="manager._id"
                            :label="
                              manager.first_name + ' ' + manager.last_name
                            "
                          >
                            <span style="float: left">{{
                              manager.first_name + " " + manager.last_name
                            }}</span>
                            <span
                              v-if="
                                manager && manager.user_type == 'SALES_DIRECTOR'
                              "
                              style="
                                float: right;
                                color: #eb5757;
                                font-size: 13px;
                                padding-left: 10px;
                              "
                              >{{ userTypeMap[manager.user_type] }}</span
                            >
                          </el-option>
                        </el-select>
                        <p
                          class="err"
                          v-if="
                            getUserAccountErrors &&
                            getUserAccountErrors.reporting_to
                          "
                        >
                          {{ getUserAccountErrors.reporting_to }}
                        </p>
                      </el-form-item>
                    </div>
                  </el-col>
                  <el-col
                    :span="8"
                    v-if="
                      formData.user_type &&
                      formData.user_type == 'HOSPITAL_MARKETING_MANAGER'
                    "
                  >
                    <div class="case-creation__input">
                      <el-form-item class="mb-15">
                        <label for="text"> Sales Director </label>
                        <el-select
                          v-model="formData.reporting_to"
                          placeholder="Select Sales Director"
                          :filterable="true"
                          multiple
                          collapse-tags
                          default-first-option
                        >
                          <el-option
                            v-for="(sales, index) of salesUsersList"
                            :key="index"
                            :value="sales._id"
                            :label="sales.first_name + ' ' + sales.last_name"
                          >
                            <span style="float: left">{{
                              sales.first_name + " " + sales.last_name
                            }}</span>
                          </el-option>
                        </el-select>
                        <p
                          class="err"
                          v-if="
                            getUserAccountErrors &&
                            getUserAccountErrors.reporting_to
                          "
                        >
                          {{ getUserAccountErrors.reporting_to }}
                        </p>
                      </el-form-item>
                    </div>
                  </el-col>
                </el-row>
                <el-row
                  :gutter="20"
                  v-show="
                    (formData.hospital || formData.lab) && formData.user_type
                  "
                >
                  <el-col :span="4">
                    <div class="case-creation__input">
                      <el-form-item class="mb-15">
                        <label for="text">
                          First Name
                          <span class="star">*</span>
                        </label>

                        <el-input
                          type="text"
                          v-model="formData.first_name"
                          placeholder="Enter First Name"
                        ></el-input>
                        <span
                          class="err"
                          v-if="
                            getUserAccountErrors &&
                            getUserAccountErrors.first_name
                          "
                          >{{ this.getUserAccountErrors.first_name }}</span
                        >
                      </el-form-item>
                    </div>
                  </el-col>
                  <el-col :span="4">
                    <div class="case-creation__input">
                      <el-form-item class="mb-15">
                        <label for="text">
                          Last Name
                          <span class="star">*</span>
                        </label>

                        <el-input
                          type="text"
                          v-model="formData.last_name"
                          placeholder="Enter Last Name"
                        ></el-input>
                        <span
                          class="err"
                          v-if="
                            getUserAccountErrors &&
                            getUserAccountErrors.last_name
                          "
                          >{{ this.getUserAccountErrors.last_name }}</span
                        >
                      </el-form-item>
                    </div>
                  </el-col>
                  <el-col :span="4">
                    <div class="case-creation__input">
                      <el-form-item class="mb-15">
                        <label for="text">
                          Email
                          <span class="star">*</span>
                        </label>

                        <el-input
                          type="email"
                          class="email-lower"
                          v-model="formData.email"
                          placeholder="Enter Email"
                        ></el-input>
                        <span
                          class="err"
                          v-if="
                            getUserAccountErrors && getUserAccountErrors.email
                          "
                          >{{ this.getUserAccountErrors.email }}</span
                        >
                      </el-form-item>
                    </div>
                  </el-col>
                  <el-col :span="4">
                    <div class="case-creation__input">
                      <el-form-item class="mb-15">
                        <label for="text">
                          Phone
                          <span class="star">*</span>
                        </label>

                        <el-input
                          type="text"
                          v-model="formData.phone"
                          placeholder="Enter Phone"
                          maxlength="10"
                        ></el-input>
                        <span
                          class="err"
                          v-if="
                            getUserAccountErrors && getUserAccountErrors.phone
                          "
                          >{{ getUserAccountErrors.phone }}</span
                        >
                      </el-form-item>
                    </div>
                  </el-col>
                  <el-col :span="4">
                    <div class="case-creation__input">
                      <el-form-item class="mb-15">
                        <label for="text">
                          User Name
                          <span class="star">*</span>
                        </label>

                        <el-input
                          class="username"
                          type="text"
                          v-model="formData.username"
                          placeholder="User Name"
                        >
                        </el-input>

                        <span
                          class="err"
                          v-if="
                            getUserAccountErrors &&
                            getUserAccountErrors.username
                          "
                          >{{ this.getUserAccountErrors.username }}</span
                        >
                      </el-form-item>
                      <span
                        class="err"
                        v-if="
                          getUserAccountErrors &&
                          getUserAccountErrors.critical_error
                        "
                        >{{ this.getUserAccountErrors.critical_error }}</span
                      >
                    </div>
                  </el-col>
                  <el-col
                    :span="4"
                    v-if="formData.user_type === 'LAB_TECHNOLOGIST'"
                  >
                    <div class="case-creation__input">
                      <el-form-item class="mb-15">
                        <label for="text">
                          Case Type
                          <span class="star">*</span>
                        </label>

                        <el-select
                          multiple
                          filterable
                          v-model="formData.case_types"
                          placeholder="Select Case type"
                          collapse-tags
                          v-on:keypress="isLetter($event)"
                        >
                          <el-option
                            v-for="(caseType, index) of getCaseTypes"
                            :key="index"
                            :value="caseType.value"
                            :label="caseType.case_name"
                          ></el-option>
                        </el-select>

                        <span
                          class="err"
                          v-if="
                            getUserAccountErrors &&
                            getUserAccountErrors.allowed_case_types
                          "
                          >{{
                            this.getUserAccountErrors.allowed_case_types
                          }}</span
                        >
                      </el-form-item>
                    </div>
                  </el-col>
                  <el-col  :span="4"
                    v-if="
                      formData.user_type == 'HOSPITAL_ACCOUNT_MANAGER' &&
                      getProfileDesignationForHeader === 'LAB_SUPER_ADMIN'
                    "
                  >
                    <div class="radio-buttons-alignments">
                      <div class="case-creation__input">
                        <label for="mt-2"> Physician Signature </label>
                        <el-form-item class="text mb-0" :required="true">
                          <el-radio-group v-model="formData.is_SuperAdmin">
                            <div class="d-flex">
                              <el-radio :label="true" class="">Yes</el-radio>
                              <el-radio :label="false">No</el-radio>
                            </div>
                          </el-radio-group>
                        </el-form-item>
                      </div>
                    </div>
                  </el-col>
                </el-row>
              </el-form>
            </div>
          </div>
          <div>
            <el-row
              class="borderBotton"
              :gutter="20"
              v-show="(formData.hospital || formData.lab) && formData.user_type"
            >
              <el-col :span="18">
                <div
                  class="email-events"
                  v-if="emailEvents && emailEvents.length"
                >
                  <div class="email-label">
                    <label>Email Events</label>

                    <div class="check-box-group">
                      <el-checkbox-group
                        v-model="emailEventsData"
                        class="search-checkbox"
                      >
                        <el-checkbox
                          v-for="(emailEvent, index) in emailEvents"
                          :label="emailEvent.name"
                          :key="index"
                        >
                          {{
                            captilize(emailEvent.name.split("_").join(" "))
                          }}</el-checkbox
                        >
                      </el-checkbox-group>
                    </div>
                  </div>
                </div>
              </el-col>
              <el-col :span="6">
                <div
                  class="case-creation__actions"
                  style="
                    display: flex;
                    justify-content: flex-end;
                    padding: 10px 10px 10px 0;
                  "
                >
                  <ul class="list pb-0 pt-0">
                    <li>
                      <el-button
                        size="medium"
                        type="default"
                        class="btn cancel-button"
                        @click="cancelUser()"
                        >Cancel</el-button
                      >
                    </li>
                    <li>
                      <el-button
                        size="medium"
                        type="success"
                        class="btn save-button"
                        @click="onSubmit"
                      >
                        {{ isNewUser ? "Add Account" : "Update Account" }}
                      </el-button>
                    </li>
                  </ul>
                </div>
              </el-col>
            </el-row>
          </div>
        </div>
        <div class="case-creation__card" v-if="isSelectedHospitalUser">
          <div class="case-creation__heading">
            <div class="title-with-icon">
              <span class="icon">
                <img
                  src="@/assets/images/icons/facilities_icon.svg"
                  alt="icon"
                  class="img-fluid"
                />
              </span>
              <h3 class="title">Ordering Facilities</h3>
            </div>
          </div>
          <div class="case-creation__body">
            <el-row gutter="100" v-loading="hospitalsLoading">
              <!-- Available Hospitals -->
              <el-col :span="12">
                <div class="selectedFacilities">
                  <h3 class="selectedHeader">list of Ordering Facilities</h3>
                  <div class="cardBody">
                    <div class="search-filter">
                      <el-input
                        v-model="searchQuery"
                        placeholder="Search for a facility..."
                        clearable
                        @input="filterHospitals"
                      ></el-input>
                      <span>
                        <img
                          src="@/assets/images/icons/search-icon-with-theme.svg"
                          alt="icon"
                          class="img-fluid icon"
                        />
                      </span>
                    </div>
                    <div>
                      <ul>
                        <li
                          v-for="hospital in filteredHospitals"
                          :key="hospital._id"
                        >
                          <el-checkbox
                            class="search-checkbox top-5"
                            :checked="isHospitalSelected(hospital)"
                            @change="() => toggleHospitalSelection(hospital)"
                            :disabled="isAbleToUpdateFacility"
                            :title="isAbleToUpdateFacility ? 'Please reach out to the SUPER ADMIN for re-assigning facilities' : ''"
                          >
                            {{ hospital.name }}
                          </el-checkbox>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </el-col>
              <!-- Selected Hospitals -->
              <el-col :span="12">
                <div class="selectedFacilities">
                  <h3 class="selectedHeader">List of Facilities Assigned</h3>
                  <div class="cardBody">
                    <div class="search-filter">
                      <el-input
                        v-model="searchSelectedQuery"
                        placeholder="Search facilities..."
                        clearable
                      ></el-input>
                      <span>
                        <img
                          src="@/assets/images/icons/search-icon-with-theme.svg"
                          alt="icon"
                          class="img-fluid icon"
                        />
                      </span>
                    </div>
                    <div class="selectedCheckBox">
                      <el-checkbox-group 
                        v-model="formData.hospitals"
                      >
                        <el-checkbox
                          class="search-checkbox"
                          v-for="(hospital, index) in filteredSelectedHospitals"
                          :key="index"
                          :label="hospital._id"
                          :disabled="isAbleToUpdateFacility"
                          :title="isAbleToUpdateFacility ? 'Please reach out to the SUPER ADMIN for re-assigning facilities' : ''"
                        >
                          <div class="checkBoxAlignment">
                            {{ hospital.name }}
                          </div>
                        </el-checkbox>
                      </el-checkbox-group>
                    </div>
                  </div>
                </div>
                <span
                  class="err"
                  v-if="getUserAccountErrors && getUserAccountErrors.hospitals"
                  >{{ this.getUserAccountErrors.hospitals }}</span
                >
              </el-col>
            </el-row>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import UserPermissionsHelper from "@/mixins/UserPermissionsHelper";
import caseDataHelper from "@/mixins/caseDataHelper";
import AppHelper from "@/mixins/AppHelper";
import userConstants from "@/config/constants/userConstants";
import caseConstants from "@/config/constants/caseConstants";
import {} from "@/helpers/notifications";
import {
  successNotification,
  errorNotification,
} from "@/helpers/notifications";
export default {
  name: "profile",
  mixins: [UserPermissionsHelper, AppHelper, caseDataHelper],
  computed: {
    getCaseTypes() {
      return this.search_case_types;
    },
    ...mapGetters("hospitals", ["getAllHospitals"]),
    ...mapGetters("physicians", ["getAllPhysicians"]),
    ...mapGetters("userAccounts", [
      "getUserAccountAddStatus",
      "getUserAccountUpdateStatus",
      "getUserAccountErrors",
      "getEmailNotification",
      "getUserAccountDataById",
      "getAllUsers",
    ]),
    ...mapGetters("labs", ["getAllLabs"]),
    ...mapGetters("auth", ["getAuthenticatedUser", "getUserPrimaryLabId"]),
    ...mapGetters("profile", ["getProfileDesignationForHeader"]),
    isLabUser() {
      return userConstants.LAB_USER_TYPES.some(user => user.value === this.formData.user_type);
    },
    isSelectedHospitalUser() {
      return userConstants.HOSPITAL_USER_TYPES.some(user => user.value === this.formData.user_type);
    },

    getUserTypes() {
      return this.userTypes
        .map(userType => ({
          ...userType,
          options: userType.options.filter(userOptions =>
            userOptions.allow_to_add.includes(this.currentUserType)
          ),
        }))
        .filter(userType => userType.allow_to_add.includes(this.currentUserType));
    },

    checkHospitalDisable() {
      if (this.isHospitalUser) return true;
      if (!this.isNewUser && ["LAB_ADMIN", "LAB_MANAGER"].includes(this.currentUserType)) return false;
      return this.isNewUser ? false : true;
    },

    availableHospitals() {
      const hospitals = this.hospitalsData?.data ?? [];
      return this.formData?.hospitals
        ? hospitals.filter(hospital => !this.formData.hospitals.includes(hospital._id))
        : hospitals;
    },

    selectedHospitals() {
      const hospitals = this.hospitalsData?.data ?? [];
      return this.formData?.hospitals
        ? hospitals.filter(hospital => this.formData.hospitals.includes(hospital._id))
        : [];
    },

    filteredHospitals() {
      return this.searchQuery
        ? this.availableHospitals.filter(hospital =>
            hospital.name.toLowerCase().includes(this.searchQuery.toLowerCase())
          )
        : this.availableHospitals;
    },

    filteredSelectedHospitals() {
      const searchSelectedQueryLower = this.searchSelectedQuery.toLowerCase();
      return this.selectedHospitals.filter(hospital =>
        hospital.name.toLowerCase().includes(searchSelectedQueryLower)
      );
    },

    isDisabled() {
      if (!this.isNewUser && this.userId && this.hasPermission("notify_rejection", "add")) return false;
      return this.isNewUser ? false : true;
    },

    isFacilityAdmin() {
      return this.formData.user_type === "HOSPITAL_ADMIN";
    },

    isAbleToUpdateFacility() {
      return this.$route.params.user_id && !this.isSuperAdmin;
    }
  },
  data() {
    return {
      emailEvents: [],
      loading: false,
      isNewUser: true,
      userId: "",
      formData: {
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        username: "",
        user_type: "",
        hospital: "",
        physician: "",
        lab: "",
        code: "",
        hospitals: [],
        userEmailEvents: [],
        case_types: [],
        labs: [],
        is_SuperAdmin: false,
      },
      userTypes: userConstants.GROUPED_USER_TYPES,
      disableUserAdd: false,
      loadingText: "",
      emailEventsData: [],
      search_case_types: [],
      hospitalsData: {},
      physiciansData: {},
      managersList: [],
      reporting_to: [],
      userTypeMap: userConstants.userTypeMap,
      salesUsersList: [],
      billingUsers: userConstants.BILLING_USER_TYPES,
      selectedHospital: null,
      preventSelectChange: false,
      // selectedHospitals: [],
      searchQuery: "",
      searchSelectedQuery: "",
      hospitalsLoading: false,
      accountManagersList: [],
      facilityUsers: userConstants.FACILITY_USER_TYPES,
      is_SuperAdmin: false,
    };
  },
  async mounted() {
    this.userId = this.$route.params.user_id;
    this.formData.lab = this.getUserPrimaryLabId;
    if (
      this.getAuthenticatedUser.labs &&
      this.getAuthenticatedUser.labs.length == 0
    ) {
      this.formData.labs = this.getUserPrimaryLabId;
    }
    await this.fetchManagerAccounts();
    this.checkUserTypeAndFetchData();
    await this.checkAndFetchUser();
    this.refreshErrors();
    this.getLabCaseTypes();
  },
  methods: {
    async fetchManagerAccounts() {
      try {
        let params = {
          get_all: true,
          user_types: [
            "HOSPITAL_MARKETING_MANAGER",
            "SALES_DIRECTOR",
            "ACCOUNT_MANAGER",
          ],
          order_by: "first_name",
          order_type: "asc",
          status:"ACTIVE"
        };
        await this.$store.dispatch("userAccounts/fetchAllUserAccounts", params);
        if (this.getAllUsers && this.getAllUsers.data) {
          this.managersList = this.getAllUsers.data;
        }
        let usersForTestPerofmed = ["SALES_DIRECTOR"];
        this.salesUsersList = this.getAllUsers.data.filter((item) =>
          usersForTestPerofmed.includes(item.user_type)
        );
        let accountUser = ["ACCOUNT_MANAGER"];
        this.accountManagersList = this.getAllUsers.data.filter((item) =>
          accountUser.includes(item.user_type)
        );
      } catch (error) {
        this.loading = false;
        console.log(error);
      }
    },
    getLabCaseTypes() {
      if (
        this.getAuthenticatedUser &&
        this.getAuthenticatedUser.user_type == "LAB_TECHNOLOGIST"
      ) {
        this.search_case_types = this.getUserBasedCaseTypes(
          caseConstants.CASE_TYPES_DATA
        );
      } else {
        this.search_case_types = this.getLabBasedCaseTypes(
          caseConstants.CASE_TYPES_DATA
        );
      }
    },
    async fetchEmailNotification() {
      try {
        this.emailEventsData = [];
        await this.$store.dispatch("userAccounts/fetchEmailNotification", {
          user_type: this.formData.user_type,
        });
        if (
          this.getEmailNotification &&
          this.getEmailNotification.email_events
        ) {
          this.emailEvents = this.getEmailNotification.email_events;
        }
      } catch (error) {
        console.log(error);
      }
    },
    cancelUser() {
      if (this.$route.query && this.$route.query.redirectRoute) {
        this.$router.push({
          name: "userAccounts",
          path: "/user-accounts",
          query: this.$route.query,
        });
      } else {
        this.$router.push({
          name: "userAccounts",
          path: "/user-accounts",
        });
      }
    },
    convertToLower(value) {
      if (value) {
        this.patientInformationFormData.email = value.toLowerCase();
      }
    },
    async checkUserTypeAndFetchData() {
      if (this.isHospitalAdmin()) {
        // mixin method
        this.formData.hospitals = this.currentUserHospitalsData;
      }
      this.fetchAllHospitals();
      this.fetchLabs();
    },

    async checkAndFetchUser() {
      try {
        if (this.userId) {
          this.loading = true;
          this.isNewUser = false;
          this.loadingText = "Fetching data.........";

          await this.$store.dispatch("userAccounts/fetchUserAccountById", {
            id: this.userId,
          });
          let data = { ...this.getUserAccountDataById };
          if (data) {
            this.setUserDetailsInForm(data);
          }
          if (this.formData.user_type == "HOSPITAL_PHYSICIAN") {
            await this.loadAllPhysicians();
          }
          this.loading = false;
        }
      } catch (error) {
        console.log(error);
      }
    },

    async refreshErrors() {
      await this.$store.dispatch("userAccounts/refreshErrors");
    },

    async fetchAllHospitals() {
      try {
        this.hospitalsLoading = true;
        await this.$store.dispatch("hospitals/fetchAllHospitals", {
          get_all: true,
        });
        this.hospitalsData = JSON.parse(JSON.stringify(this.getAllHospitals));
        this.hospitalsLoading = false;
      } catch (error) {
        console.log(error);
      }
    },

    async fetchLabs() {
      try {
        await this.$store.dispatch("labs/fetchAllLabs", {
          get_all: true,
        });
      } catch (error) {
        console.log(error);
      }
    },

    async onSubmit() {
      if (this.isNewUser) {
        await this.addAccount();
      } else {
        await this.updateAccount();
      }
    },
    prepareParams() {
      let params = {
        first_name: this.formData.first_name.trim(),
        last_name: this.formData.last_name.trim(),
        email: this.formData.email ? this.formData.email.toLowerCase() : "",
        phone: this.formData.phone,
        allow_unathorized_access: this.formData.is_SuperAdmin,
        username:
          this.formData && this.formData.username
            ? this.convertToLowerCase(this.formData.username.trim())
            : undefined,
        user_type: this.formData.user_type,
        allowed_case_types: this.formData.case_types,
        physician: this.formData.physician
          ? this.formData.physician
          : undefined,
        settings: {
          email_events: [],
        },
      };
      if (this.userId) {
        params.id = this.userId;
        params.settings = { ...this.getUserAccountDataById.settings };
      }
      if (this.isSelectedHospitalUser) {
        params.hospitals = this.formData.hospitals;
      } else {
        params.hospitals = [];
        params.hospital = this.formData.hospital;
      }

      if (this.emailEventsData && this.emailEventsData.length) {
        const userSelectedEvents = this.emailEvents.filter((eventData) => {
          return this.emailEventsData.indexOf(eventData.name) != -1;
        });
        if (userSelectedEvents && userSelectedEvents.length) {
          params["settings"]["email_events"] = userSelectedEvents;
        }
      } else {
        params["settings"]["email_events"] = [];
      }
      if (this.formData.user_type && this.formData.user_type == "MARKETER") {
        params.reporting_to = this.formData.reporting_to;
      }
      if (
        this.formData.user_type &&
        this.formData.user_type == "HOSPITAL_MARKETING_MANAGER"
      ) {
        params.reporting_to = this.formData.reporting_to;
      }
      if (
        this.formData.user_type &&
        this.formData.user_type == "SALES_DIRECTOR"
      ) {
        params.account_manager = this.formData.account_manager;
      }
      return params;
    },
    async addAccount() {
      this.disableUserAdd = true;
      this.loading = true;

      try {
        let params = this.prepareParams();
        if (this.isLabUser) {
          params.lab =
            this.formData.labs && this.formData.labs.length > 1
              ? this.formData.labs[0]
              : this.formData.labs.length > 0
              ? this.formData.labs[0]
              : "";
          params.labs = this.formData.labs;
        } else {
          params.lab = this.formData.lab;
        }
        params = this.getFormattedValues(params);

        await this.$store.dispatch("userAccounts/addUserAccount", params);
        this.loading = false;
        this.disableUserAdd = false;

        if (this.getUserAccountAddStatus) {
          successNotification("User Account added successfully");
          this.cancelUser();
        } else if (
          this.getUserAccountErrors &&
          this.getUserAccountErrors.critical_error
        ) {
          errorNotification(this.getUserAccountErrors.critical_error);
        }
      } catch (err) {
        this.loading = false;
        this.disableUserAdd = false;
        console.log(err);
        this.$notify.error({
          title: "Error",
          message: "Error at Adding user account",
        });
      }
    },
    async updateAccount() {
      this.disableUserAdd = true;
      this.loading = true;

      try {
        let params = this.prepareParams();
        if (this.isLabUser) {
          params.lab =
            this.formData.labs && this.formData.labs.length > 1
              ? this.formData.labs[0]
              : this.formData.labs.length > 0
              ? this.formData.labs[0]
              : "";
          params.labs = this.formData.labs;
        } else {
          params.lab = this.formData.lab;
        }
        params = this.getFormattedValues(params);

        await this.$store.dispatch("userAccounts/updateUserAccount", params);
        this.loading = false;
        this.disableUserAdd = false;
        if (this.getUserAccountUpdateStatus) {
          successNotification("User Account updated successfully");
          this.cancelUser();
        } else if (
          this.getUserAccountErrors &&
          this.getUserAccountErrors.critical_error
        ) {
          errorNotification(this.getUserAccountErrors.critical_error);
        }
      } catch (err) {
        this.loading = false;
        this.disableUserAdd = false;
        console.log(err);
        this.$notify.error({
          title: "Error",
          message: "Error at Updating user account",
        });
      }
    },
    async setUserDetailsInForm(data) {
      this.formData.first_name = data.first_name;
      this.formData.is_SuperAdmin = data.allow_unathorized_access; 
      this.formData.last_name = data.last_name;
      this.formData.email = data.email;
      this.formData.lab = data.lab ? data.lab._id : "";
      this.formData.labs = data.labs ? data.labs : "";
      this.formData.hospital = data.hospital ? data.hospital._id : "";
      this.formData.hospitals = Array.isArray(data.hospitals)
        ? data.hospitals.map((hospital) => hospital._id)
        : [];

      this.formData.physician = data.physician || "";
      this.formData.user_type = data.user_type;
      this.formData.phone = data.phone;
      this.formData.username = data.username;
      this.formData.case_types = data.allowed_case_types;
      this.formData.reporting_to = Array.isArray(data.reporting_to)
        ? data.reporting_to.map((manager) => manager)
        : [];

      this.formData.account_manager = Array.isArray(data.account_manager)
        ? data.account_manager.map((manager) => manager)
        : [];

      await this.fetchEmailNotification();

      if (
        data.settings &&
        data.settings.email_events &&
        Array.isArray(data.settings.email_events)
      ) {
        this.emailEventsData = data.settings.email_events.flatMap(
          (eventData) => eventData.name
        ) || [];
      } else {
        this.emailEventsData = [];
      }

      if (this.formData.physician) {
        this.loadAllPhysicians(this.formData.hospital);
      }
    },
    async loadAllPhysicians(hospitalId) {
      try {
        let params = {
          hospital: hospitalId,
          get_all: true,
        };
        await this.$store.dispatch("physicians/fetchAllPhysicians", params);
        this.physiciansData = JSON.parse(JSON.stringify(this.getAllPhysicians));
      } catch (error) {
        console.log(error);
      }
    },

    async setLabDetails(labId) {
      let selectedLab = this.getAllLabs.find((lab) => lab._id == labId);
      if (selectedLab && selectedLab.lab_code) {
        this.formData.code = "." + selectedLab.lab_code.toLowerCase();
      }
      this.formData.physician = null;
      this.formData.lab = labId;
    },
    async checkUserType(userType) {
      if (this.isNewUser) {
        if (userType === "HOSPITAL_PHYSICIAN") {
          await this.loadAllPhysicians();
        }
        await this.fetchEmailNotification();
        if (
          this.getEmailNotification &&
          this.getEmailNotification.email_events &&
          this.getEmailNotification.email_events.length
        ) {
          this.emailEventsData = this.emailEvents.flatMap(
            (eventData) => eventData.name
          );
        } else {
          this.emailEventsData = [];
        }
        this.refreshErrors();
        this.formData.first_name = "";
        this.formData.last_name = "";
        this.formData.email = "";
        this.formData.phone = "";
        this.formData.username = "";
        this.formData.hospitals = [];
        this.formData.physician = "";
        if (this.isHospitalAdmin() && userType == "HOSPITAL_ACCOUNT_MANAGER") {
          // method from mixin
          this.formData.hospitals = this.currentUserHospitalsData;
        }
      }
    },
    setPhysicianData(physicianId) {
      let selectedPhysician = this.getAllPhysicians.data.find(
        (physician) => physician._id == physicianId
      );
      console.log(selectedPhysician);
      this.formData.first_name = selectedPhysician.name.split(" ")[0];
      this.formData.last_name = selectedPhysician.name.split(" ")[1];
      this.formData.email = selectedPhysician.email;
      this.formData.phone = selectedPhysician.phone;
      this.formData.username = "";
      this.formData.hospitals = selectedPhysician.hospitals;
    },
    convertToLowerCase(data = "") {
      return data.toLowerCase();
    },
    isHospitalSelected(hospital) {
      return this.selectedHospitals.some((h) => h._id === hospital._id);
    },
    toggleHospitalSelection(hospital) {
      if (this.isFacilityAdmin) {
        // Restrict to one facility for Facility Admin
        this.formData.hospitals = [hospital._id];
      } else {
        const index = this.selectedHospitals.findIndex(h => h._id === hospital._id);
        if (index === -1) {
          this.selectedHospitals.push(hospital);
        } else {
          this.selectedHospitals.splice(index, 1);
        }
        this.formData.hospitals = this.selectedHospitals.map(h => h._id);
      }
    },
    removeHospital(hospital) {
      const index = this.formData.hospitals.indexOf(hospital._id);
      if (index !== -1) {
        this.formData.hospitals.splice(index, 1);
      }
    },
  },
};
</script>

<style lang="scss">
#add-user-block {
  .email-events {
    display: flex;
    align-items: center;
    position: relative;
    padding: 0.5rem 1rem;
    .email-label {
      padding-right: 10px;
      color: #0a5da6;
      font-size: 15px;
      font-weight: 600;
      display: flex;
      align-items: center;
    }
    .check-box-group {
      .search-checkbox {
        margin-left: 23px;
        .el-checkbox {
          height: 35px;
        }

        .el-checkbox__input .el-checkbox__inner {
          border-color: transparent;
          box-shadow: 0 0 0 1px #ced1d6;
          border-radius: 4px;
          width: 17px;
          height: 17px;
        }

        .el-checkbox__input.is-checked .el-checkbox__inner {
          background: #0a5da6;
          border-color: #ffffff;
          width: 17px;
          height: 17px;
          box-shadow: 0 0 0 1px #ced1d6;
        }

        .el-checkbox__label {
          color: #34495e;
        }
      }
      .el-checkbox__inner::after {
        box-sizing: content-box;
        content: "";
        border: 2px solid var(--el-checkbox-checked-icon-color);
        border-left: 0;
        border-top: 0;
        height: 9px;
        left: 5.5px;
        position: absolute;
        top: 1px;
        transform: rotate(45deg) scaleY(1);
        width: 3px;
        transition: transform 0.15s ease-in 50ms;
        transform-origin: center;
      }
    }
  }
  .borderBotton {
    border-top: 1px solid #e7e7f7;
    align-items: center;
  }
  .gridAuto {
    display: flex;
    // grid-template-columns: 1fr 2fr;
  }
  .selectedFacilities {
    border: 1.2px solid #e7e7f7;
    border-radius: 6px;
    overflow: hidden;
    .selectedHeader {
      background-color: #e7e7f7;
      color: #273480;
      text-transform: uppercase;
      padding: 0.3rem 1rem;
      font-size: 15px;
    }
    .cardBody {
      padding: 0.5rem 1rem;
      ul,
      .selectedCheckBox {
        height: 300px;
        overflow: auto;
        margin-top: 0.5rem;
        @media (min-width: 1280px) and (max-width: 1600px) {
          height: 160px;
        }
      }
      .search-filter {
        min-height: 33.5px;
        max-height: 33px;
        background: #ffffff;
        border: 0.5px solid #e0e0e0;
        border-radius: 4px;
        min-width: 90%;
        color: #000000;
        padding: 0 10px;
        line-height: 1;
        display: flex;
        align-items: center;

        input {
          min-height: 24px;
          max-height: 32px;
          border: none;
          padding: 0;
          color: #000000;
        }

        .icon {
          width: 22px;
          margin-left: 0.5em;
          line-height: 1;
          margin-bottom: -3px;
        }
      }
      .checkBoxAlignment {
        display: flex;
        align-items: center;
        img {
          position: relative;
          top: 1px;
          left: 5px;
          height: 16px;
          width: 16px;
        }
      }
      .search-checkbox {
        margin-left: 3px;
        margin-bottom: 0;
        position: relative;
        top: 3px;
        width: 90%;
        height: 26px;

        .el-checkbox {
          height: 22px;
        }

        .el-checkbox__input .el-checkbox__inner {
          border-color: transparent;
          box-shadow: 0 0 0 1px #ced1d6;
          border-radius: 4px;
          width: 17px;
          height: 17px;
        }

        .el-checkbox__input.is-checked .el-checkbox__inner {
          background: #0a5da6;
          border-color: #ffffff;
          box-shadow: 0 0 0 1px #ced1d6;
          width: 17px;
          height: 17px;
        }

        .el-checkbox__inner::after {
          box-sizing: content-box;
          content: "";
          border: 2px solid var(--el-checkbox-checked-icon-color);
          border-left: 0;
          border-top: 0;
          height: 9px;
          left: 5px;
          border-radius: 1px;
          position: absolute;
          top: 1px;
          transform: rotate(45deg) scaleY(1);
          width: 3px;
          transition: transform 0.15s ease-in 50ms;
          transform-origin: center;
        }

        .el-checkbox__label {
          color: #34495e;
          font-size: 12px;
        }
      }
    }
  }
  .username {
    .el-input__inner {
      text-transform: lowercase !important;
    }
  }
}
</style>